import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, qs } from "@/lib/api";
import type { Contact } from "@/lib/types";
import { formatAgo, formatDateTime } from "@/lib/format";
import { Empty } from "@/components/PageHeader";
import { useToast } from "@/components/Toaster";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { Table, TD, TH, THead, TR } from "@/components/ui/table";
import { LoadError } from "@/components/LoadError";

interface LoginStatus {
  contact_id: number;
  has_password: boolean;
  password_source?: string;
  password_set_at?: string;
  last_login_at?: string;
  failed_attempts: number;
  locked_until?: string;
  sites: { site: string; name: string; member: boolean }[];
  access_keys: string[];
}

interface Attempt {
  id: number;
  at: string;
  site_key: string;
  method: string;
  email: string | null;
  outcome: string;
  ip: string | null;
}

interface LinkInfo {
  id: string;
  site: string;
  purpose: string;
  created_at: string;
  created_by: string;
  sent_via: string;
  expires_at: string;
  uses: number;
  max_uses: number;
  last_used_at?: string;
  revoked_at?: string;
}

const sourceLabel: Record<string, string> = {
  keap_field98: "imported from Keap",
  set_by_user: "chosen by them",
  reset: "reset by them",
  admin: "set by staff",
  genpass: "from the site's password tool",
};

/** Sign-ins for the Club, Manager Training and courses (crm-sites): status, unlock, recent attempts and links. */
export function SignInsCard({ c }: { c: Contact }) {
  const status = useQuery({
    queryKey: ["login-status", c.id],
    queryFn: () => api<LoginStatus>(`/api/v1/logins/status/${c.id}`),
    retry: false,
  });
  const qc = useQueryClient();
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const unlock = useMutation({
    mutationFn: () => api(`/api/v1/logins/status/${c.id}/unlock`, { method: "POST" }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["login-status", c.id] });
      toast("Unlocked: they can sign in again now");
    },
    onError: (e) => toast(e.message, "error"),
  });
  const s = status.data;
  const locked = s?.locked_until && new Date(s.locked_until).getTime() > Date.now();
  const memberOf = (s?.sites ?? []).filter((x) => x.member);
  return (
    <Card>
      <CardHeader>
        <CardTitle>Sign-ins</CardTitle>
        <button className="text-xs text-[var(--color-primary)] hover:underline" onClick={() => setOpen(true)}>
          Attempts and links
        </button>
      </CardHeader>
      <CardContent className="space-y-1.5 text-sm">
        {status.isLoading && <p className="text-[var(--color-muted-foreground)]">Checking…</p>}
        {status.error && <p className="text-[var(--color-destructive)]">{status.error.message}</p>}
        {s && (
          <>
            <div className="flex flex-wrap gap-1">
              {memberOf.length === 0 && <span className="text-[var(--color-muted-foreground)]">Not a member of a sign-in site.</span>}
              {memberOf.map((m) => (
                <Badge key={m.site} variant="success">
                  {m.name}
                </Badge>
              ))}
            </div>
            <Row k="Password" v={s.has_password ? sourceLabel[s.password_source ?? ""] ?? "set" : "none yet"} />
            <Row k="Last signed in" v={s.last_login_at ? formatAgo(s.last_login_at) : "never"} title={formatDateTime(s.last_login_at)} />
            {s.failed_attempts > 0 && <Row k="Wrong passwords" v={String(s.failed_attempts)} />}
            {locked && (
              <div className="flex items-center justify-between gap-2 rounded-md bg-[var(--color-warning-bg)] px-2 py-1 text-xs text-[var(--color-warning-fg)]">
                Locked until {formatDateTime(s.locked_until)}
                <Button size="sm" variant="outline" disabled={unlock.isPending} onClick={() => unlock.mutate()}>
                  Unlock
                </Button>
              </div>
            )}
          </>
        )}
      </CardContent>
      <SignInSheet c={c} open={open} onOpenChange={setOpen} />
    </Card>
  );
}

function Row({ k, v, title }: { k: string; v: string; title?: string }) {
  return (
    <div className="flex justify-between gap-3" title={title}>
      <span className="text-[var(--color-muted-foreground)]">{k}</span>
      <span className="text-right">{v}</span>
    </div>
  );
}

const outcomeLabel: Record<string, string> = {
  success: "signed in",
  bad_password: "wrong password",
  unknown_email: "unknown email",
  no_password: "no password set",
  not_a_member: "not a member",
  locked: "locked out",
  rate_limited: "too many tries",
  expired: "link expired",
  used_up: "link already used",
  revoked: "link cancelled",
  bad_link: "bad link",
  weak_password: "password too short",
};

const outcomeVariant = (o: string): "success" | "destructive" | "warning" | "muted" =>
  o === "ok" || o === "success" ? "success" : o.includes("locked") || o.includes("rate") ? "warning" : o.includes("wrong") || o.includes("fail") || o.includes("bad") ? "destructive" : "muted";

function SignInSheet({ c, open, onOpenChange }: { c: Contact; open: boolean; onOpenChange: (o: boolean) => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const attempts = useQuery({ queryKey: ["login-attempts", c.id], queryFn: () => api<{ attempts: Attempt[] }>("/api/v1/logins/attempts" + qs({ contact_id: c.id, limit: 100 })), enabled: open });
  const links = useQuery({ queryKey: ["login-links", c.id], queryFn: () => api<{ links: LinkInfo[] }>(`/api/v1/logins/links/${c.id}`), enabled: open });
  const revoke = useMutation({
    mutationFn: (linkId?: string) => api<{ revoked: number }>("/api/v1/logins/links/revoke", { json: linkId ? { link_id: linkId } : { contact_id: c.id } }),
    onSuccess: (r) => {
      void qc.invalidateQueries({ queryKey: ["login-links", c.id] });
      toast(`Cancelled ${r.revoked} link${r.revoked === 1 ? "" : "s"}`);
    },
    onError: (e) => toast(e.message, "error"),
  });
  const live = (links.data?.links ?? []).filter((l) => !l.revoked_at && new Date(l.expires_at).getTime() > Date.now() && l.uses < l.max_uses);
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent title="Sign-in attempts and links" description="Newest first, Mountain Time. Use it to answer “why can't I get in?”">
        <section className="border-b">
          <div className="flex items-center justify-between px-4 py-2">
            <h4 className="text-sm font-semibold">Sign-in links sent</h4>
            {live.length > 0 && (
              <Button size="sm" variant="ghost" className="text-[var(--color-destructive)]" onClick={() => window.confirm("Cancel every sign-in link of theirs that still works?") && revoke.mutate(undefined)}>
                Cancel all working links
              </Button>
            )}
          </div>
          {links.error ? (
            <LoadError what="sign-in links" error={links.error} onRetry={() => links.refetch()} />
          ) : links.isLoading ? null : (links.data?.links ?? []).length === 0 ? (
            <Empty title="No tracked links." />
          ) : (
            <Table>
              <THead>
                <tr>
                  <TH>Made</TH>
                  <TH>Site</TH>
                  <TH>Kind</TH>
                  <TH>Used</TH>
                  <TH>Status</TH>
                  <TH />
                </tr>
              </THead>
              <tbody>
                {links.data!.links.map((l) => {
                  const working = live.includes(l);
                  return (
                    <TR key={l.id}>
                      <TD className="whitespace-nowrap" title={`by ${l.created_by} via ${l.sent_via}`}>
                        {formatDateTime(l.created_at)}
                      </TD>
                      <TD>{l.site}</TD>
                      <TD>{l.purpose}</TD>
                      <TD className="num">
                        {l.uses}/{l.max_uses}
                      </TD>
                      <TD>{l.revoked_at ? <Badge variant="muted">cancelled</Badge> : working ? <Badge variant="success">works until {formatDateTime(l.expires_at)}</Badge> : <Badge variant="muted">expired or used</Badge>}</TD>
                      <TD>
                        {working && (
                          <Button size="sm" variant="ghost" onClick={() => revoke.mutate(l.id)}>
                            Cancel
                          </Button>
                        )}
                      </TD>
                    </TR>
                  );
                })}
              </tbody>
            </Table>
          )}
        </section>
        <section>
          <h4 className="px-4 py-2 text-sm font-semibold">Attempts</h4>
          {attempts.error ? (
            <LoadError what="sign-in attempts" error={attempts.error} onRetry={() => attempts.refetch()} />
          ) : attempts.isLoading ? null : (attempts.data?.attempts ?? []).length === 0 ? (
            <Empty title="No sign-in attempts recorded." />
          ) : (
            <Table>
              <THead>
                <tr>
                  <TH>When</TH>
                  <TH>Site</TH>
                  <TH>How</TH>
                  <TH>Result</TH>
                  <TH>From</TH>
                </tr>
              </THead>
              <tbody>
                {attempts.data!.attempts.map((a) => (
                  <TR key={a.id}>
                    <TD className="num whitespace-nowrap">{formatDateTime(a.at)}</TD>
                    <TD>{a.site_key}</TD>
                    <TD>{a.method.replace(/_/g, " ")}</TD>
                    <TD>
                      <Badge variant={outcomeVariant(a.outcome)}>{outcomeLabel[a.outcome] ?? a.outcome.replace(/_/g, " ")}</Badge>
                    </TD>
                    <TD className="num text-xs">{a.ip ?? "—"}</TD>
                  </TR>
                ))}
              </tbody>
            </Table>
          )}
        </section>
      </SheetContent>
    </Sheet>
  );
}
